import { useState } from 'react'
import type { CrmMapping } from '../lib/db'

interface Props {
  mapping: CrmMapping
  onPick: (crmRecordId: string, name: string, email: string | null) => Promise<void>
  onSkip: () => Promise<void>
}

export default function CrmMatchPicker({ mapping, onPick, onSkip }: Props) {
  const [busyId, setBusyId] = useState<string | null>(null)
  const [skipping, setSkipping] = useState(false)

  const candidates = mapping.candidates ?? []

  const handlePick = async (c: { crmRecordId: string; name: string; email: string | null }) => {
    setBusyId(c.crmRecordId)
    try { await onPick(c.crmRecordId, c.name, c.email) }
    finally { setBusyId(null) }
  }

  const handleSkip = async () => {
    setSkipping(true)
    try { await onSkip() }
    finally { setSkipping(false) }
  }

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-medium text-amber-900">
          Multiple Affinity matches for {mapping.crmContactName}. Pick one to sync:
        </div>
        <button
          onClick={handleSkip}
          disabled={skipping}
          className="rounded px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50"
        >
          {skipping ? 'Skipping...' : "Don't sync this contact"}
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {candidates.map((c) => (
          <button
            key={c.crmRecordId}
            onClick={() => handlePick(c)}
            disabled={busyId === c.crmRecordId}
            className="rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-left text-xs hover:bg-amber-50 disabled:opacity-50"
          >
            <div className="font-medium text-zinc-900">{c.name}</div>
            {c.email && <div className="text-zinc-500">{c.email}</div>}
            {c.currentOrganization && <div className="text-zinc-400">{c.currentOrganization}</div>}
          </button>
        ))}
      </div>
    </div>
  )
}
